'use client';

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";

export default function About() {
  const t = useTranslations('about');
  const [about, setAbout] = useState<any>({
    title: t('title'),
    description: t('description'),
    buildingImage: '',
    mission: t('mission.content'),
    vision: t('vision.content'),
    values: t('values.items'),
    teamMembers: t('team.items')
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await fetch('/api/about');
        if (!response.ok) {
          throw new Error('Failed to fetch about info');
        }
        const data = await response.json();
        setAbout(data);
      } catch (err) {
        console.error('Error fetching about info:', err);
        // 保持使用翻译文件中的默认内容
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
  }, [t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* 页面标题 */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center text-black">{about.title}</h1>
          <p className="text-center text-black mt-4">{about.description.substring(0, 100)}...</p>
        </div>
      </section>

      {/* 公司简介 */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <h2 className="text-3xl font-bold mb-6 text-black">{about.title}</h2>
              <p className="text-black mb-4">
                {about.description}
              </p>
            </div>
            <div className="md:w-1/2">
              <Image 
                src={about.buildingImage || "/images/home/company-building.svg"} 
                alt={about.title} 
                width={600} 
                height={400} 
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 发展历程 */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-black">发展历程</h2>
          <div className="relative max-w-3xl mx-auto">
            {/* 时间轴 */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>
            
            {/* 2010年 */}
            <div className="relative mb-12">
              <div className="flex items-center">
                <div className="w-1/2 pr-8 text-right">
                  <h3 className="text-xl font-semibold text-blue-600">2022</h3>
                  <p className="text-black">公司成立</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-600 rounded-full border-4 border-white"></div>
                <div className="w-1/2 pl-8"></div>
              </div>
            </div>
            
            {/* 2015年 */}
            <div className="relative mb-12">
              <div className="flex items-center">
                <div className="w-1/2 pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-600 rounded-full border-4 border-white"></div>
                <div className="w-1/2 pl-8">
                  <h3 className="text-xl font-semibold text-blue-600">2023</h3>
                  <p className="text-black">业务扩展</p>
                </div>
              </div>
            </div>
            
            {/* 2020年 */}
            <div className="relative mb-12">
              <div className="flex items-center">
                <div className="w-1/2 pr-8 text-right">
                  <h3 className="text-xl font-semibold text-blue-600">2024</h3>
                  <p className="text-black">产品升级</p>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-600 rounded-full border-4 border-white"></div>
                <div className="w-1/2 pl-8"></div>
              </div>
            </div>
            
            {/* 2023年 */}
            <div className="relative">
              <div className="flex items-center">
                <div className="w-1/2 pr-8"></div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-blue-600 rounded-full border-4 border-white"></div>
                <div className="w-1/2 pl-8">
                  <h3 className="text-xl font-semibold text-blue-600">2025</h3>
                  <p className="text-black">国际化战略</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 核心团队 */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-black">核心团队</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {about.teamMembers.map((member: any, index: number) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center">
                <Image 
                  src={member.image || "https://via.placeholder.com/128"} 
                  alt={member.name} 
                  width={128} 
                  height={128} 
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                <p className="text-blue-600 mb-4">{member.position}</p>
                <p className="text-black">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 企业文化 */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-black">企业文化</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">公司使命</h3>
              <p className="text-black">
                {about.mission}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 text-2xl">🌟</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">公司愿景</h3>
              <p className="text-black">
                {about.vision}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-blue-600 text-2xl">💡</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">核心价值观</h3>
              <ul className="text-black space-y-2">
                {about.values.map((value: string, index: number) => (
                  <li key={index}>{value}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}